import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ChefHat, Clock, Heart, Utensils, CheckCircle, Users } from "lucide-react";
import { DISPLAY_NAME, MAX_HOUSEHOLD_BY_TIER } from "../../billing/tiers";
import type { SubscriptionTier } from "../../types/subscription";

interface LandingPageProps {
  onGetStarted: () => void;
  onSeeHowItWorks: () => void;
}

export function LandingPage({ onGetStarted, onSeeHowItWorks }: LandingPageProps) {
  const features = [
    {
      icon: <ChefHat className="w-8 h-8 text-warning" />,
      title: "Knows What You'll Love", 
      description: "Every recipe recommendation gets better at matching your taste, dietary needs, and cooking style"
    },
    {
      icon: <Clock className="w-8 h-8 text-warning" />,
      title: "Saves You Time",
      description: "From planning to shopping to prep - we handle the thinking so you can focus on cooking"
    },
    {
      icon: <Heart className="w-8 h-8 text-warning" />,
      title: "Actually Enjoyable",
      description: "No more decision fatigue or food waste. Just meals you'll love making and eating"
    }
  ];

  const tierConfigs: Array<{ tier: SubscriptionTier; price: number; description: string; features: string[]; badge?: string; popular?: boolean; trial?: string }> = [
    {
      tier: 'demo',
      price: 0,
      description: 'Try the experience before upgrading',
      features: [
        `Plans for up to ${MAX_HOUSEHOLD_BY_TIER.demo} people`,
        'Preview the recipe library',
        'Basic shopping list exports',
        'Community tips and tricks',
      ],
      badge: 'Start Here',
      trial: 'Always free'
    },
    {
      tier: 'starter',
      price: 19.99,
      description: 'Built for busy households',
      features: [
        `Weekly plans for up to ${MAX_HOUSEHOLD_BY_TIER.starter} people`,
        'Smart shopping lists',
        'Recipe saving & collections',
        'Equipment-aware instructions',
      ],
      badge: 'Most Popular',
      popular: true,
      trial: '7-day free trial'
    },
    {
      tier: 'core_plus',
      price: 29.99,
      description: 'For AI-powered planners and big crews',
      features: [
        `Scaling for up to ${MAX_HOUSEHOLD_BY_TIER.core_plus} people`,
        'AI meal personalization',
        'Batch prep guides',
        'Unlimited swaps & plans',
      ],
      badge: 'Best Value',
      trial: '7-day free trial'
    }
  ];

  const pricingTiers = tierConfigs.map((tier) => ({
    name: DISPLAY_NAME[tier.tier],
    price: tier.price === 0 ? 'Free' : `$${tier.price.toFixed(2)}`,
    period: tier.price === 0 ? '' : 'month',
    description: tier.description,
    features: tier.features,
    badge: tier.badge,
    popular: tier.popular,
    trial: tier.trial,
    tier: tier.tier,
  }));

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="border-b border-border/20 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold text-dusk">nura</div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onSeeHowItWorks}>
              How it works
            </Button>
            <Button onClick={onGetStarted}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-dusk mb-6 leading-tight">
            Meal planning that
            <br />
            <span className="text-warning">actually works</span>
          </h1>
          <p className="text-xl md:text-2xl text-supporting mb-8 max-w-2xl mx-auto leading-relaxed">
            Weekly meal plans with beautiful recipes that actually fit your taste, schedule, and budget
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              onClick={onGetStarted}
              className="bg-coral desktop-hover:bg-coral/90 text-ink px-8 py-4 text-lg font-medium min-w-[200px]"
            >
              Start Your Free Week
            </Button>
            <Button 
              variant="secondary" 
              size="lg" 
              onClick={onSeeHowItWorks}
              className="bg-stone desktop-hover:bg-stone/80 text-ink px-8 py-4 text-lg min-w-[200px]"
            >
              See How It Works
            </Button>
          </div>
          <p className="text-sm text-supporting mt-4">
            No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-16 bg-white/40" data-section="features">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-dusk mb-4">
            Nourishment made simple
          </h2>
          <p className="text-xl text-supporting text-center mb-12 max-w-2xl mx-auto">
            Like having a personal chef who knows exactly what you want to eat
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white border-0 shadow-cardShadow rounded-[var(--radius-card)] p-8 text-center desktop-hover:shadow-cardHover transition-shadow">
                <CardContent className="p-0">
                  <div className="mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-dusk mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-supporting leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-dusk mb-4">
            Choose your plan
          </h2>
          <p className="text-xl text-supporting text-center mb-12">
            Start free, upgrade when you're ready
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`bg-white border-0 shadow-cardShadow rounded-[var(--radius-card)] p-6 relative ${
                  tier.popular ? 'ring-2 ring-warning' : ''
                }`}
              >
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-coral text-ink">
                    {tier.badge}
                  </Badge>
                )}
                <CardContent className="p-0">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-dusk mb-2">
                      {tier.name}
                    </h3>
                    <div className="mb-2">
                      <span className="text-3xl font-bold text-dusk">{tier.price}</span>
                      {tier.period && (
                        <span className="text-supporting">/{tier.period}</span>
                      )}
                    </div>
                    <p className="text-sm text-supporting">
                      {tier.description}
                    </p>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-sage flex-shrink-0" />
                        <span className="text-ink">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full ${
                      tier.popular 
                        ? 'bg-coral desktop-hover:bg-coral/90 text-ink' 
                        : 'bg-stone desktop-hover:bg-stone/80 text-ink'
                    }`}
                    onClick={onGetStarted}
                  >
                    {tier.tier === 'demo' ? 'Start Free Preview' : 'Get Started'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="px-4 py-16 bg-white/40">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-8 mb-8">
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-warning" />
              <span className="text-dusk font-medium">2,000+ home cooks</span>
            </div>
            <div className="flex items-center gap-2">
              <Utensils className="w-6 h-6 text-warning" />
              <span className="text-dusk font-medium">10,000+ meals planned</span>
            </div>
          </div>
          <blockquote className="text-xl text-dusk italic mb-4">
            "It's like nura reads my mind. Every recipe is something I actually want to cook."
          </blockquote>
          <cite className="text-supporting">— Sarah K., busy parent</cite>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-2xl font-bold text-dusk mb-2">nura</div>
            <p className="text-supporting">Nourishment made simple</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h4 className="font-semibold text-dusk mb-3">Product</h4>
              <ul className="space-y-2 text-supporting">
                <li><a href="#" className="desktop-hover:text-ink transition-colors">How it works</a></li>
                <li><a href="/pricing" className="desktop-hover:text-ink transition-colors">Pricing</a></li>
                <li><a href="#" className="desktop-hover:text-ink transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-dusk mb-3">Company</h4>
              <ul className="space-y-2 text-supporting">
                <li><a href="#" className="desktop-hover:text-ink transition-colors">About</a></li>
                <li><a href="#" className="desktop-hover:text-ink transition-colors">Blog</a></li>
                <li><a href="#" className="desktop-hover:text-ink transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-dusk mb-3">Support</h4>
              <ul className="space-y-2 text-supporting">
                <li><a href="#" className="desktop-hover:text-ink transition-colors">Help Center</a></li>
                <li><a href="#" className="desktop-hover:text-ink transition-colors">Privacy</a></li>
                <li><a href="#" className="desktop-hover:text-ink transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-border/20 mt-8 pt-8 text-center text-supporting">
            <p>&copy; 2024 nura. Made with care for home cooks everywhere.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
